import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  FlatList,
  Image,
  SafeAreaView,
} from "react-native";
import { postFeeLookup } from "../../../../../services/common.service";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { ProductType } from "../../../../../view-models/productType";
import type { FeeInfo } from "../../../../../view-models/feeInfo";
import { FeeLookupHelper } from "../../../../../helper/feeLookupHelper";
import { useGlobalError }  from '../../../../uiComponents/GlobalErrorProvider';
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import RadioSelectedIcon from "../../../../../../../../assets/images/Radio-Select-Button.png";

// import { Ionicons } from "@expo/vector-icons"; // or react-native-vector-icons

// Server-driven options will be shown here

type HowTheyReceiveStep = {
  onCountrySelected?: () => void;
};


export default function HowTheyReceiveStep({ onCountrySelected }: HowTheyReceiveStep) {
  const { t } = useTranslation();
  const { showError } = useGlobalError();
  const [selected, setSelected] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [feeInfos, setFeeInfos] = useState<FeeInfo[]>([]);
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const selectedDestinationCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const selectedDestinationStateAbbr = useSessionStore((s: SessionState) => s.selectedDestinationStateAbbr);
  const selectedReceiveCurrency = useSessionStore((s: SessionState) => (s as any).selectedReceiveCurrency);
  const setSelectedSendOption = useSessionStore((s: SessionState) => (s as any).setSelectedSendOption);
  const setPendingSendOption = useSessionStore((s: SessionState) => (s as any).setPendingSendOption);
  const setSelectedDeliveryOptionStore = useSessionStore((s: SessionState) => (s as any).setSelectedDeliveryOptionStore);
  const lastFeeLookupResponse = useSessionStore((s: SessionState) => (s as any).lastFeeLookupResponse);

  const getOptionKey = (fee: FeeInfo): string => {
    return JSON.stringify({
      sendCurrency: fee?.SendAmounts?.SendCurrency || null,
      exchangeRate: FeeLookupHelper.getExchangeRate(fee) || null,
      receiveCurrency: FeeLookupHelper.getReceiveCurrency(fee) || null,
      deliveryOptDisplayName: fee.DeliveryOptDisplayName || null,
      receiveAgentId: (fee as any).ReceiveAgentId || null,
    });
  };

  const formatExchangeRate = (fee: FeeInfo): string => {
    const sendCurrency = fee?.SendAmounts?.SendCurrency || 'USD';
    const rate = FeeLookupHelper.getExchangeRate(fee);
    const receiveCurrency = FeeLookupHelper.getReceiveCurrency(fee);
    const rateNum = Number(rate);
    const rateFixed = isFinite(rateNum) ? rateNum.toFixed(4) : String(rate);
    return `1 ${sendCurrency} = ${rateFixed} ${receiveCurrency}`;
  };

  const formatFee = (fee: FeeInfo): string => {
    const amount = Number(fee?.SendAmounts?.TotalSendFees || 0);
    const currency = fee?.SendAmounts?.SendCurrency || 'USD';
    return `Fee: ${amount.toFixed(2)} ${currency}`;
  };

  // Utility function to group by a "composite key"
function groupBy<T, K>(array: T[], keyGetter: (item: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>();
  array.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

  useEffect(() => {
    const useOrFetch = async () => {
      if (!agentId || !unitProfileId || !selectedDestinationCountryCode) return;
      try {
        setIsLoading(true);
        setError(null);
        let result: any = lastFeeLookupResponse;
        if (!result) {
          const payload: any = {
            agentID: agentId,
            unitProfileID: unitProfileId,
            amount: 1,
            sendCurrency: 'USD',
            destinationCountry: selectedDestinationCountryCode,
            productType: ProductType.SEND,
            allOptions: true,
          };
          if ((selectedDestinationCountryCode === 'USA' || selectedDestinationCountryCode === 'CAN') && selectedDestinationStateAbbr) {
            payload.destinationState = selectedDestinationStateAbbr;
          }
          result = await postFeeLookup(payload);
        }

        console.log('HowTheyReceiveStep FeeLookup result (reuse if available):', result);
        // Unwrap nested data if needed and derive array
        let inner: any = result;
        if (typeof result?.data === 'string') {
          try { inner = JSON.parse(result.data); } catch { inner = result; }
        } else if (typeof result?.data === 'object') {
          inner = result.data;
        }
        const arr: FeeInfo[] = Array.isArray(inner?.FeeInfos) ? inner.FeeInfos : [];

        // Build sendOptions from parsed array
        const sendOptions: FeeInfo[] = (() => {
          // Prefer first non-USD receive currency from the response
          const nonUsdCurrency = (() => {
            const first = arr.find((f: FeeInfo) => {
              const rc = (FeeLookupHelper.getReceiveCurrency(f) || '').toUpperCase();
              return rc && rc !== 'USD';
            });
            return first ? (FeeLookupHelper.getReceiveCurrency(first) || '').toUpperCase() : '';
          })();

          const filtered = arr.filter(
            (fee: FeeInfo) =>
              fee.ErrorInfo == null &&
              fee.SendAmounts != null &&
              (!nonUsdCurrency || (FeeLookupHelper.getReceiveCurrency(fee) || '').toUpperCase() === nonUsdCurrency)
          );

          const grouped = groupBy(filtered, (f: FeeInfo) =>
            JSON.stringify({
              sendCurrency: f.SendAmounts!.SendCurrency,
              exchangeRate: FeeLookupHelper.getExchangeRate(f),
              receiveCurrency: FeeLookupHelper.getReceiveCurrency(f),
              deliveryOptDisplayName: f.DeliveryOptDisplayName,
              totalSendFees: f.SendAmounts!.TotalSendFees,
              receiveAgentName: f.ReceiveAgentName,
            })
          );

          return Array.from(grouped.values()).map((group) => group[0]);
        })();

        console.log('HowTheyReceiveStep sendOptions:', sendOptions);
        setFeeInfos(sendOptions);
      } catch (e: any) {
        console.error('FeeLookup failed in HowTheyReceiveStep:', e);
        setError('Failed to load delivery options');
      } finally {
        setIsLoading(false);
      }
    };
    useOrFetch();
  }, [agentId, unitProfileId, selectedDestinationCountryCode, selectedDestinationStateAbbr, lastFeeLookupResponse]);


  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("RECEIVE_METHOD_TITLE")}</Text>
      <Text style={styles.subtitle}>Enter your address</Text>
        {isLoading && (
          <>
            <Text style={{ marginBottom: 8 }}>{t("RECEIVE_METHOD_LOADING")}</Text>
          </>
        )}
        {error && (
          <>
            <Text style={{ marginBottom: 8, color: 'red' }}>{error}</Text>
            <Text style={{ marginBottom: 8, color: 'red' }}>{t("RECEIVE_METHOD_ERROR")}</Text>
          </>
        )}
        <FlatList
          data={feeInfos}
          keyExtractor={(item) => getOptionKey(item)}
          contentContainerStyle={{ paddingBottom: 100 }}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={styles.methodRow}
              onPress={() => {
                const key = getOptionKey(item);
                setSelected(key);
                // Stage selection; will be committed on global Continue click
                console.log('item===>', item);
                onCountrySelected?.();
                setPendingSendOption(item);
                try {
                  setSelectedDeliveryOptionStore?.({
                    DeliveryOption: (item as any)?.DeliveryOption ?? (item as any)?.DeliveryOptId ?? null,
                    option: item,
                  });
                } catch {}
              }}
            >
              <View style={{ flex: 1 }}>
                <Text style={styles.methodTitle}>{item.DeliveryOptDisplayName || item.ReceiveAgentName || t("RECEIVE_METHOD_DELIVERY_OPTION")}</Text>
                <Text style={styles.methodDetails}>{formatExchangeRate(item)}</Text>
                <Text style={styles.methodDetails}>{formatFee(item)}</Text>
                {!!(item.ReceiveAgentAbbreviation) && (
                  <Text style={styles.methodDetails}>{item.ReceiveAgentAbbreviation}</Text>
                )}
              </View>
              {/* <View
                style={[
                  styles.radioOuter,
                  selected === getOptionKey(item) && styles.radioOuterSelected,
                ]}
              >
                {selected === getOptionKey(item) && <View style={styles.radioInner} />}
              </View> */}
              <View style={styles.radioContainer}>
                <View style={[styles.radioButton]}>
                  {selected === getOptionKey(item) && (
                    <Image
                      source={RadioSelectedIcon}
                      style={{ height: 20, width: 20}}   
                      resizeMode='contain'     
                    />
                  )}
                </View>
              </View>
            </TouchableOpacity>
          )}
          ListEmptyComponent={!isLoading && !error ? (
            <Text style={{ color: '#6B7280' }}>{t("RECEIVE_METHOD_NO_OPTIONS")}</Text>
          ) : null}
        />
        <Text style={styles.methodDetails}><Text style={{ fontWeight: '700'}}>Transfer speed </Text>depends on the delivery method, transaction review, and system availability of MoneyGram and its partners.</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  },
  methodRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  methodTitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 20,
    color: Colors.titleTextColor,
    marginBottom: 4
  },
  methodDetails: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
    color: Colors.titleTextColor,
    marginBottom: 4
  },
  providersRow: { flexDirection: "row", marginTop: 6 },
  providerCircle: { fontSize: 20, marginRight: 4 },
  moreCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#6B7280",
    alignItems: "center",
    justifyContent: "center",
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#D1D5DB",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 8,
    marginTop: 4,
  },
  radioOuterSelected: { borderColor: "#000" },
  radioInner: { width: 10, height: 10, borderRadius: 5, backgroundColor: "#000" },
  footer: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    padding: 16,
    backgroundColor: "#fff",
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  continueBtn: {
    backgroundColor: "#111",
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  continueText: { color: "#fff", fontSize: 16, fontWeight: "600" },
  radioContainer: {
    marginLeft: 12,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#D1D1D1'
  },
});